// File: frontend/src/services/adminTopicOpsService.ts

import apiClient from "./apiClient";

/**
 * Admin "Taşı / Birleştir" (move / merge) API service.
 *
 * Endpoints (see AdminTopicOpsController):
 *   GET  /api/admin/topic-ops/search
 *   GET  /api/admin/topic-ops/entries
 *   GET  /api/admin/topic-ops/merge-history
 *   POST /api/admin/topic-ops/merge          (ADMIN only, irreversible)
 *   POST /api/admin/topic-ops/move-entries   (ADMIN + MODERATOR)
 */

export type TopicOpsType = "MATCH" | "CLUB" | "TEAM" | "PERSON" | "CUSTOM_TOPIC";

export interface TopicOpsTarget {
  id: number;
  type: TopicOpsType;
  title: string;
  // Frontend path fragment; TEAM rows are composite "clubSlug/teamSlug"
  slug: string;
  entryCount: number;
  active: boolean;
  // True only for a user's personal nickaltı custom topic
  nickalti: boolean;
  // True when the topic was already absorbed by a previous merge
  merged: boolean;
}

export interface TopicOpsEntry {
  id: number;
  snippet: string;
  authorUsername: string;
  createdAt: string;
  // True when the entry is currently soft-deleted (still movable)
  deleted: boolean;
}

export interface TopicOpsEntryPage {
  content: TopicOpsEntry[];
  currentPage: number;
  totalItems: number;
  totalPages: number;
}

export interface TopicMergeResult {
  movedEntryCount: number;
  movedDraftCount: number;
  targetSlug: string;
  targetTitle: string;
}

export interface TopicMergeHistoryItem {
  id: number;
  topicType: TopicOpsType;
  sourceId: number;
  targetId: number;
  sourceSlug: string;
  targetSlug: string;
  sourceTitle: string | null;
  targetTitle: string | null;
  movedEntryCount: number;
  mergedBy: number;
  mergedByUsername: string | null;
  mergedAt: string;
}

/** Topic autocomplete (min 2 chars enforced server-side). */
export const searchTopics = (type: TopicOpsType, q: string): Promise<TopicOpsTarget[]> =>
  apiClient.get<TopicOpsTarget[]>(
    `/api/admin/topic-ops/search?type=${type}&q=${encodeURIComponent(q)}`
  );

/** Paginated entries of one topic — the move screen's picker. */
export const listTopicEntries = (
  type: TopicOpsType,
  topicId: number,
  page: number,
  size: number = 20
): Promise<TopicOpsEntryPage> =>
  apiClient.get<TopicOpsEntryPage>(
    `/api/admin/topic-ops/entries?type=${type}&topicId=${topicId}&page=${page}&size=${size}`
  );

/** Newest 50 merge operations for the history table. */
export const getMergeHistory = (): Promise<TopicMergeHistoryItem[]> =>
  apiClient.get<TopicMergeHistoryItem[]>(`/api/admin/topic-ops/merge-history`);

/** Merge source into target. ADMIN only. Irreversible. */
export const mergeTopics = (
  topicType: TopicOpsType,
  sourceId: number,
  targetId: number
): Promise<TopicMergeResult> =>
  apiClient.post<TopicMergeResult>(`/api/admin/topic-ops/merge`, {
    topicType,
    sourceId,
    targetId,
  });

/** Move entries to a target topic (cross-type allowed). ADMIN + MODERATOR. */
export const moveEntries = (
  entryIds: number[],
  targetType: TopicOpsType,
  targetId: number
): Promise<{ movedCount: number }> =>
  apiClient.post<{ movedCount: number }>(`/api/admin/topic-ops/move-entries`, {
    entryIds,
    targetType,
    targetId,
  });